// Phase 1a — Asset detail page.
// Layout follows DroneFleet/DronePage/DronePage.js: vertical stack of cards
//   [Header] → [Status + flag actions] → [History] → [Components] → [Info + extension] → [Actions]
// No tabs — everything visible by scroll. Operationally friendlier than a tabs layout for
// crew picking up a handheld scanner.
//
// Extension-field editing is inline (pencil → input → blur/Enter saves). For SmartCharger
// and TimeMachine, build_phase advancement to 'in_service' triggers the DB recompute that
// flips operational_status from 'building' to 'ready' — the server-returned asset reflects this,
// so the chip updates on save without a separate refetch.

import React, { useState, useEffect, useCallback } from 'react';
import { buttonClass } from '../components/ui/Button';
import { useParams } from 'react-router-dom';
import { useApi } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import BackLink from '../components/BackLink';
import useUserStore from '../stores/useUserStore';
import useAssetsStore from '../stores/useAssetsStore';
import EntityHistory from '../components/EntityHistory/EntityHistory';
import { formatTimestamp } from '../utils/date-utils';
import {
  Asset, SmartChargerDetails, TimeMachineDetails, RtkUnitDetails, GcsDetails,
  ChildAssetSummary,
  INVENTORY_CATEGORY_LABELS,
  BUILD_PHASE_LABELS, BUILD_PHASE_ORDER,
} from './types';
import { AssetStatusTag, InventoryCategoryTag } from './StatusTags';
import Tooltip from '../components/ui/Tooltip';
import Reveal from '../components/ui/Reveal';
import FadeIn from '../components/ui/FadeIn';
import AssignBarcodeModal from './AssignBarcodeModal';
import RaiseFlagModal from './RaiseFlagModal';
import LogMaintenanceModal from './LogMaintenanceModal';
import AddComponentModal from './AddComponentModal';
import InlineEditField from './InlineEditField';
import PageLayout from '../components/ui/PageLayout';
import LogoSpinner from '../components/ui/LogoSpinner';
import EmptyState from '../components/ui/EmptyState';
import { Panel } from '../components/ui/Panel';
import { ListRow, ListStack } from '../components/ui/ListRow';

const BUILD_PHASE_OPTIONS = BUILD_PHASE_ORDER.map(v => ({ value: v, label: BUILD_PHASE_LABELS[v] }));
const POWER_OPTIONS = [
  { value: 'regular', label: 'Regular' },
  { value: 'super',   label: 'Super' },
];

const AssetDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const api = useApi();
  const toast = useToast();
  const hasCapability = useUserStore((s) => s.hasCapability);
  const ensureTypesLoaded = useAssetsStore((s) => s.ensureTypesLoaded);

  const [asset, setAsset] = useState<Asset | null>(null);
  const [loading, setLoading] = useState(true);
  const [historyRefresh, setHistoryRefresh] = useState(0);

  const [showAssignBarcode, setShowAssignBarcode]   = useState(false);
  const [flagModalSeverity, setFlagModalSeverity]   = useState<'Flag' | 'OOC' | null>(null);
  const [showLogMaintenance, setShowLogMaintenance] = useState(false);
  const [showAddComponent, setShowAddComponent]     = useState(false);

  const canEdit = hasCapability('operations', 'manufacturing');

  // api/toast deliberately excluded from deps — useApi() returns a new reference each render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchAsset = useCallback(async () => {
    try {
      const data: Asset = await api.get(`/assets/${id}`);
      setAsset(data);
    } catch {
      toast.error('Failed to load asset');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchAsset(); }, [fetchAsset]);

  // Warm the asset-types cache so AddComponentModal renders labels on direct-nav loads
  // (user deep-links to /assets/:id without passing through the landing page).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { ensureTypesLoaded().catch(() => {}); }, []);

  const refreshAfterMutation = async () => {
    await fetchAsset();
    setHistoryRefresh(n => n + 1);
  };

  // Shared save handler for inline-edit fields. Takes the extension-field key and returns a
  // Promise-returning onSave that PATCHes just that field. The server returns the hydrated
  // asset (with any derived-status changes), which we swap into local state atomically.
  const saveExtensionField = (key: string) => async (v: unknown) => {
    try {
      const updated: Asset = await api.patch(`/assets/${asset!.id}`, { body: { extension: { [key]: v } } });
      setAsset(updated);
      // build_phase changes write a row to asset_build_phase_history via DB trigger AND
      // can flip operational_status (in_service → ready). The PATCH response carries the
      // recomputed spine, but some downstream displays (header chip, status banner, the
      // history timeline) only see the change after a full hydrate fetch. Refetching the
      // asset + bumping the history refreshKey keeps the whole page coherent.
      if (key === 'build_phase') {
        setHistoryRefresh(n => n + 1);
        await refreshAfterMutation();
      }
    } catch (err) {
      toast.error(`Couldn't save ${key.replace(/_/g, ' ')}`);
      throw err;
    }
  };

  // Same shape for spine-level patches (notes, custom_fields) — shares the PATCH endpoint.
  const saveSpineField = (key: string) => async (v: unknown) => {
    try {
      const updated: Asset = await api.patch(`/assets/${asset!.id}`, { body: { [key]: v } });
      setAsset(updated);
    } catch (err) {
      toast.error(`Couldn't save ${key.replace(/_/g, ' ')}`);
      throw err;
    }
  };

  const handleClearAllFlags = async () => {
    try {
      const result = await api.post(`/assets/${id}/clear-all-flags`, { body: {} });
      toast.success(`Cleared ${result.cleared} flag(s)`);
      await refreshAfterMutation();
    } catch {
      toast.error('Failed to clear flags');
    }
  };

  const handleRetireToggle = async () => {
    if (!asset) return;
    try {
      await api.patch(`/assets/${asset.id}`, { body: { is_active: !asset.is_active } });
      toast.success(asset.is_active ? 'Asset retired' : 'Asset restored');
      await refreshAfterMutation();
    } catch {
      toast.error('Failed to update');
    }
  };

  const handleDetachChild = async (childId: string, childDisplayCode: string) => {
    try {
      await api.patch(`/assets/${childId}`, { body: { parent_asset_id: null } });
      toast.success(`${childDisplayCode} detached`);
      await refreshAfterMutation();
    } catch {
      toast.error('Failed to detach component');
    }
  };

  const handleMarkUpdated = async () => {
    if (!asset) return;
    try {
      await api.post(`/assets/${asset.id}/mark-updated`, { body: {} });
      toast.success('Marked updated');
      await refreshAfterMutation();
    } catch {
      toast.error('Failed to mark updated');
    }
  };

  if (loading) return <LogoSpinner label="Loading asset" />;
  if (!asset) return <EmptyState variant="area">Asset not found</EmptyState>;

  return (
    <PageLayout width="narrow" kind="record" className="pt-4" header={<BackLink to="/assets" label="Assets" className="mb-3" />}>
      <AssetHeader asset={asset} />
      <AssetStatus
        asset={asset}
        canEdit={canEdit}
        onFlag={() => setFlagModalSeverity('Flag')}
        onOOC={() => setFlagModalSeverity('OOC')}
        onClearAll={handleClearAllFlags}
        onMarkUpdated={handleMarkUpdated}
      />
      <EntityHistory entityType="asset" entityId={asset.id} refreshKey={historyRefresh} onMutate={refreshAfterMutation} />
      {asset.asset_type?.category === 'kit' && (
        <AssetComponents
          components={asset.children ?? []}
          canEdit={canEdit}
          onAttach={() => setShowAddComponent(true)}
          onDetach={handleDetachChild}
        />
      )}
      <AssetInfo asset={asset} canEdit={canEdit}
        saveExtensionField={saveExtensionField}
        saveSpineField={saveSpineField}
      />
      <AssetActions
        asset={asset}
        canEdit={canEdit}
        onAssignBarcode={() => setShowAssignBarcode(true)}
        onLogMaintenance={() => setShowLogMaintenance(true)}
        onRetireToggle={handleRetireToggle}
      />

      {showAssignBarcode && (
        <AssignBarcodeModal
          assetId={asset.id}
          currentBarcode={asset.barcode}
          displayCode={asset.display_code}
          onClose={() => setShowAssignBarcode(false)}
          onSuccess={async () => { setShowAssignBarcode(false); await refreshAfterMutation(); }}
        />
      )}
      {flagModalSeverity && (
        <RaiseFlagModal
          assetId={asset.id}
          statusdesc={flagModalSeverity}
          onClose={() => setFlagModalSeverity(null)}
          onSuccess={async () => { setFlagModalSeverity(null); await refreshAfterMutation(); }}
        />
      )}
      {showLogMaintenance && (
        <LogMaintenanceModal
          assetId={asset.id}
          onClose={() => setShowLogMaintenance(false)}
          onSuccess={async () => { setShowLogMaintenance(false); await refreshAfterMutation(); }}
        />
      )}
      {showAddComponent && (
        <AddComponentModal
          parentId={asset.id}
          parentDisplayCode={asset.display_code}
          onClose={() => setShowAddComponent(false)}
          onSuccess={async () => { setShowAddComponent(false); await refreshAfterMutation(); }}
        />
      )}
    </PageLayout>
  );
};

// ---------------------------------------------------------------------------
// Header card — display_code + type label + status + inventory_category chip.

const AssetHeader: React.FC<{ asset: Asset }> = ({ asset }) => {
  const type = asset.asset_type;
  return (
    <Panel label="Asset" className="mb-3">
      <div className="flex justify-between items-start gap-3">
        <div>
          <h1 className="text-2xl font-bold text-primary font-mono">{asset.display_code}</h1>
          <div className="text-on-surface-variant text-sm mt-1">{type?.label ?? 'Unknown Type'}</div>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <AssetStatusTag status={asset.operational_status} size="md" />
          {asset.inventory_category && (
            <Tooltip label="Inventory category, managed by finance workflows (Phase 1c)">
              <InventoryCategoryTag
                category={asset.inventory_category}
                label={INVENTORY_CATEGORY_LABELS[asset.inventory_category]}
                size="md"
              />
            </Tooltip>
          )}
        </div>
      </div>
    </Panel>
  );
};

// ---------------------------------------------------------------------------
// Status + flag actions. Flag/OOC buttons half-width, side by side (DronePage pattern).
// "Clear All Flags" appears below when flagged. SmartChargers with needs_update=true get a
// third button inline to mark the charger firmware-updated.

const AssetStatus: React.FC<{
  asset: Asset;
  canEdit: boolean;
  onFlag: () => void;
  onOOC: () => void;
  onClearAll: () => void;
  onMarkUpdated: () => void;
}> = ({ asset, canEdit, onFlag, onOOC, onClearAll, onMarkUpdated }) => {
  const isFlagged = asset.operational_status === 'flagged' || asset.operational_status === 'ooc';
  const isCharger = asset.asset_type?.key === 'smart_charger';
  // on_latest_firmware === false → operator hasn't applied the latest firmware yet; show the
  // Mark Firmware Updated button so they can clear it once the update runs.
  const needsUpdate = isCharger && (asset.extension as SmartChargerDetails | null)?.on_latest_firmware === false;

  return (
    <Panel title="Status" titleStyle="section" className="mb-3">
      <div className="flex gap-3 mb-0">
        <button
          onClick={onFlag}
          disabled={!canEdit}
          className={`${buttonClass('accent', 'lg')} flex-1`}
        >
          FLAG
        </button>
        <button
          onClick={onOOC}
          disabled={!canEdit}
          className={`${buttonClass('danger-quiet', 'lg')} flex-1`}
        >
          OOC
        </button>
      </div>
      <Reveal show={!!isFlagged}>
        <button
          onClick={onClearAll}
          disabled={!canEdit}
          className={`${buttonClass('quiet', 'md')} mt-3 w-full`}
        >
          Clear All Flags
        </button>
      </Reveal>
      <Reveal show={!!needsUpdate}>
        <Tooltip label="Clear the needs-update flag and stamp firmware_version with the current global target">
          <button
            onClick={onMarkUpdated}
            disabled={!canEdit}
            className={`${buttonClass('quiet', 'md')} mt-3 w-full`}
          >
            Mark Firmware Updated
          </button>
        </Tooltip>
      </Reveal>
    </Panel>
  );
};

// ---------------------------------------------------------------------------
// Components card — child assets (parent_asset_id = this asset's id).

const AssetComponents: React.FC<{
  components: ChildAssetSummary[];
  canEdit: boolean;
  onAttach: () => void;
  onDetach: (childId: string, childDisplayCode: string) => void;
}> = ({ components, canEdit, onAttach, onDetach }) => {
  if (components.length === 0 && !canEdit) return null;

  return (
    <Panel
      title={<>Components {components.length > 0 && <span className="font-normal text-on-surface-variant">({components.length})</span>}</>}
      titleStyle="section"
      actions={canEdit ? (
        <button
          onClick={onAttach}
          className={buttonClass('accent', 'sm')}
        >
          + Attach
        </button>
      ) : undefined}
      className="mb-3"
    >
      <FadeIn key={components.length === 0 ? 'empty' : 'list'}>
      {components.length === 0 && (
        <EmptyState variant="area">No components attached</EmptyState>
      )}

      {components.length > 0 && (
        <ListStack
          items={components}
          getKey={child => child.id}
          on="panel"
          label="Components"
          renderRow={child => (
            <ListRow
              to={`/assets/${child.id}`}
              actions={canEdit ? (
                <button
                  onClick={() => onDetach(child.id, child.display_code)}
                  className={buttonClass('danger-quiet', 'sm')}
                >
                  Detach
                </button>
              ) : undefined}
            >
              <span className="flex min-w-0 items-center gap-3">
                <span className="truncate font-mono text-sm text-on-surface">{child.display_code}</span>
                <span className="truncate text-xs text-on-surface-variant">{child.asset_type_label}</span>
                <AssetStatusTag status={child.operational_status} />
              </span>
            </ListRow>
          )}
        />
      )}
      </FadeIn>
    </Panel>
  );
};

// ---------------------------------------------------------------------------
// Info card — spine fields + extension fields. Extension fields are inline-editable
// when the user has edit capability; read-only view hides pencils and skips saves.

interface SaveField { (v: unknown): Promise<void> }
interface SaveFieldFactory { (key: string): SaveField }

const AssetInfo: React.FC<{
  asset: Asset;
  canEdit: boolean;
  saveExtensionField: SaveFieldFactory;
  saveSpineField:     SaveFieldFactory;
}> = ({ asset, canEdit, saveExtensionField, saveSpineField }) => {
  const type = asset.asset_type;
  const readOnly = !canEdit;
  return (
    <Panel title="Details" titleStyle="section" className="mb-3">
      <ul className="space-y-3 text-white">
        <InfoRow label="Barcode" value={asset.barcode ?? <span className="italic text-on-surface-disabled">unlinked</span>} mono />
        <InfoRow label="Location" value={asset.location_bucket} />
        {asset.is_sold && (
          <InfoRow label="Sold" value={`Sold on ${formatTimestamp(asset.sold_at!, 'MMM D, YYYY')} (customer #${asset.sold_to_customer_id})`} />
        )}
        {!asset.is_active && <InfoRow label="Lifecycle" value={<span className="text-red-400">Retired</span>} />}
        <InfoRow label="Internal Pool" value={asset.is_internal_pool ? 'Yes' : 'No, sellable'} />
        {asset.inventory_category && <InfoRow label="Inventory Category" value={INVENTORY_CATEGORY_LABELS[asset.inventory_category]} />}
        <InlineEditField
          label="Notes"
          value={asset.notes}
          multiline
          placeholder="Anything operators should know"
          readOnly={readOnly}
          onSave={saveSpineField('notes')}
        />
        <InfoRow label="Created" value={formatTimestamp(asset.created_at, 'MMM D, YYYY HH:mm')} mono />

        {type?.requires_extension_table && asset.extension && (
          <ExtensionRows
            typeKey={type.key}
            ext={asset.extension}
            readOnly={readOnly}
            saveExtensionField={saveExtensionField}
          />
        )}
      </ul>
    </Panel>
  );
};

const InfoRow: React.FC<{ label: string; value: React.ReactNode; mono?: boolean }> = ({ label, value, mono }) => (
  <li>
    <div className="text-on-surface-variant text-xs uppercase tracking-wide mb-1">{label}</div>
    <div className={`text-white text-sm ${mono ? 'font-mono' : ''}`}>{value}</div>
  </li>
);

// Per-type extension fields. Dynamic types have inline-edit everywhere; auto-computed
// identity fields (sequence_number) stay read-only.
const ExtensionRows: React.FC<{
  typeKey: string;
  ext: NonNullable<Asset['extension']>;
  readOnly: boolean;
  saveExtensionField: SaveFieldFactory;
}> = ({ typeKey, ext, readOnly, saveExtensionField }) => {
  switch (typeKey) {
    case 'smart_charger': {
      const e = ext as SmartChargerDetails;
      return (
        <>
          {e.sequence_number !== null && <InfoRow label="Sequence #" value={e.sequence_number} mono />}
          <InlineEditField
            label="Power" value={e.charger_power} fieldType="select" options={POWER_OPTIONS}
            readOnly={readOnly} onSave={saveExtensionField('charger_power')}
          />
          <InlineEditField
            label="Build Phase" value={e.build_phase} fieldType="select" options={BUILD_PHASE_OPTIONS}
            readOnly={readOnly} onSave={saveExtensionField('build_phase')}
          />
          <InlineEditField
            label="VPN IP" value={e.vpn_ip} mono placeholder="10.8.5.20"
            readOnly={readOnly} onSave={saveExtensionField('vpn_ip')}
          />
          <InlineEditField
            label="VPN Confirmed" value={e.vpn_confirmed} fieldType="checkbox"
            readOnly={readOnly} onSave={saveExtensionField('vpn_confirmed')}
          />
          <InlineEditField
            label="On Latest Firmware" value={e.on_latest_firmware} fieldType="checkbox"
            readOnly={readOnly} onSave={saveExtensionField('on_latest_firmware')}
          />
          {e.last_telemetry_at && (
            <InfoRow label="Last Telemetry" value={formatTimestamp(e.last_telemetry_at, 'MMM D, YYYY HH:mm')} mono />
          )}
        </>
      );
    }
    case 'time_machine': {
      const e = ext as TimeMachineDetails;
      return (
        <>
          {e.sequence_number !== null && <InfoRow label="Sequence #" value={e.sequence_number} mono />}
          <InlineEditField
            label="Build Phase" value={e.build_phase} fieldType="select" options={BUILD_PHASE_OPTIONS}
            readOnly={readOnly} onSave={saveExtensionField('build_phase')}
          />
          <InlineEditField
            label="VPN IP" value={e.vpn_ip} mono placeholder="10.8.5.20"
            readOnly={readOnly} onSave={saveExtensionField('vpn_ip')}
          />
          <InlineEditField
            label="VPN Confirmed" value={e.vpn_confirmed} fieldType="checkbox"
            readOnly={readOnly} onSave={saveExtensionField('vpn_confirmed')}
          />
          <InlineEditField
            label="On Latest Firmware" value={e.on_latest_firmware} fieldType="checkbox"
            readOnly={readOnly} onSave={saveExtensionField('on_latest_firmware')}
          />
        </>
      );
    }
    case 'rtk_unit': {
      const e = ext as RtkUnitDetails;
      return (
        <InlineEditField
          label="On Latest Firmware" value={e.on_latest_firmware} fieldType="checkbox"
          readOnly={readOnly} onSave={saveExtensionField('on_latest_firmware')}
        />
      );
    }
    case 'ground_station_kit': {
      const e = ext as GcsDetails;
      return (
        <>
          <InlineEditField
            label="VPN IP" value={e.vpn_ip} mono placeholder="10.8.5.20"
            readOnly={readOnly} onSave={saveExtensionField('vpn_ip')}
          />
          {e.last_telemetry_at && (
            <InfoRow label="Last Telemetry" value={formatTimestamp(e.last_telemetry_at, 'MMM D, YYYY HH:mm')} mono />
          )}
        </>
      );
    }
    default:
      return null;
  }
};

// ---------------------------------------------------------------------------
// Bottom action bar — grouped into primary (barcode, maintenance) and destructive (retire)
// with a spacer between for scanability.

const AssetActions: React.FC<{
  asset: Asset;
  canEdit: boolean;
  onAssignBarcode: () => void;
  onLogMaintenance: () => void;
  onRetireToggle: () => void;
}> = ({ asset, canEdit, onAssignBarcode, onLogMaintenance, onRetireToggle }) => (
  <Panel title="Actions" titleStyle="section" className="mb-3">
    <div className="flex items-center justify-between gap-2 flex-wrap">
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={onAssignBarcode}
          disabled={!canEdit}
          className={buttonClass('accent', 'md')}
        >
          {asset.barcode ? 'Re-assign Barcode' : 'Assign Barcode'}
        </button>
        <button
          onClick={onLogMaintenance}
          disabled={!canEdit}
          className={buttonClass('info', 'md')}
        >
          Log Maintenance
        </button>
      </div>
      <button
        onClick={onRetireToggle}
        disabled={!canEdit}
        className={buttonClass('quiet', 'md')}
      >
        {asset.is_active ? 'Retire' : 'Restore'}
      </button>
    </div>
  </Panel>
);

export default AssetDetail;
